import XLSX from "xlsx-js-style";
import {
  buildHeaderRows,
  flattenLeafColumns,
  formatDecimalComma,
  formatReportDateRu,
  PROVISION_TOTAL_ROW,
  getComputedCellValue,
  parseDecimalComma,
} from "../../../models/provisionReport";

const headerStyle = {
  fill: { fgColor: { rgb: "D6EAF8" } },
  font: { bold: true },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: "999999" } },
    bottom: { style: "thin", color: { rgb: "999999" } },
    left: { style: "thin", color: { rgb: "999999" } },
    right: { style: "thin", color: { rgb: "999999" } },
  },
};

const totalStyle = {
  fill: { fgColor: { rgb: "D6EAF8" } },
  font: { bold: true },
  border: {
    top: { style: "thin", color: { rgb: "999999" } },
    bottom: { style: "thin", color: { rgb: "999999" } },
    left: { style: "thin", color: { rgb: "999999" } },
    right: { style: "thin", color: { rgb: "999999" } },
  },
};

const cellBorder = {
  border: {
    top: { style: "thin", color: { rgb: "CCCCCC" } },
    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
    left: { style: "thin", color: { rgb: "CCCCCC" } },
    right: { style: "thin", color: { rgb: "CCCCCC" } },
  },
};

function cellValue(raw, type) {
  if (raw === null || raw === undefined || raw === "") return "";
  if (type === "decimal" || type === "number") {
    if (typeof raw === "number") return formatDecimalComma(raw);
    return formatDecimalComma(raw);
  }
  return String(raw);
}

function sumColumn(rows, key, col) {
  return rows.reduce((acc, row) => {
    if (col?.type === "computed") {
      return acc + (getComputedCellValue(col, row.cells) || 0);
    }
    const v = row.cells?.[key];
    const n = typeof v === "number" ? v : parseDecimalComma(v);
    return acc + (n == null ? 0 : n);
  }, 0);
}

/**
 * Экспорт текущей вкладки в Excel (с заголовком и датой).
 */
export function exportProvisionReportExcel({
  title,
  reportDate,
  columns,
  rows,
  fileName,
}) {
  const leaves = flattenLeafColumns(columns);
  const headerRows = buildHeaderRows(columns);
  const depth = headerRows.length;

  const aoa = [];

  // Title
  aoa.push([{ v: title || "Обеспеченность", t: "s", s: { font: { bold: true, sz: 14 } } }]);
  aoa.push([
    {
      v: `Дата: ${formatReportDateRu(reportDate)}`,
      t: "s",
      s: { font: { italic: true } },
    },
  ]);
  aoa.push([]);

  const titleRowOffset = 3;

  // Multi-level headers + Филиалы column
  for (let r = 0; r < depth; r++) {
    const row = [];
    if (r === 0) {
      row.push({
        v: "Филиалы",
        t: "s",
        s: headerStyle,
      });
    } else {
      row.push({ v: "", t: "s", s: headerStyle });
    }
    headerRows[r].forEach((cell) => {
      row.push({ v: cell.title, t: "s", s: headerStyle });
      for (let i = 1; i < (cell.colSpan || 1); i++) {
        row.push({ v: "", t: "s", s: headerStyle });
      }
    });
    aoa.push(row);
  }

  const dataRows = rows.filter((r) => !r.isTotal);
  dataRows.forEach((row) => {
    const line = [{ v: row.branchName, t: "s", s: cellBorder }];
    leaves.forEach((col) => {
      const raw =
        col.type === "computed"
          ? getComputedCellValue(col, row.cells)
          : row.cells?.[col.key];
      line.push({
        v:
          col.type === "computed"
            ? formatDecimalComma(raw)
            : cellValue(raw, col.type),
        t: "s",
        s: cellBorder,
      });
    });
    aoa.push(line);
  });

  // ИТОГО
  const totalLine = [
    { v: PROVISION_TOTAL_ROW.branchName, t: "s", s: totalStyle },
  ];
  leaves.forEach((col) => {
    if (col.aggregate === "sum" || col.type === "computed") {
      const sum = sumColumn(dataRows, col.key, col);
      totalLine.push({
        v: formatDecimalComma(sum),
        t: "s",
        s: totalStyle,
      });
    } else {
      totalLine.push({ v: "", t: "s", s: totalStyle });
    }
  });
  aoa.push(totalLine);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // merge title
  const colCount = leaves.length + 1;
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(0, colCount - 1) } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: Math.max(0, colCount - 1) } },
  ];

  // merge Филиалы vertically
  if (depth > 1) {
    ws["!merges"].push({
      s: { r: titleRowOffset, c: 0 },
      e: { r: titleRowOffset + depth - 1, c: 0 },
    });
  }

  // merge header cells by colSpan/rowSpan
  const addMerges = (nodes, level, startCol) => {
    let col = startCol;
    (nodes || []).forEach((node) => {
      const leafCount = node.children?.length
        ? flattenLeafColumns(node.children).length
        : 1;
      const hasChildren = !!node.children?.length;
      if (leafCount > 1) {
        ws["!merges"].push({
          s: { r: titleRowOffset + level, c: col },
          e: { r: titleRowOffset + level, c: col + leafCount - 1 },
        });
      }
      if (!hasChildren && depth - level > 1) {
        ws["!merges"].push({
          s: { r: titleRowOffset + level, c: col },
          e: { r: titleRowOffset + depth - 1, c: col },
        });
      }
      if (hasChildren) {
        addMerges(node.children, level + 1, col);
      }
      col += leafCount;
    });
  };
  addMerges(columns, 0, 1);

  ws["!cols"] = [{ wch: 36 }, ...leaves.map(() => ({ wch: 14 }))];
  ws["!rows"] = [{ hpt: 24 }, { hpt: 18 }, { hpt: 8 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Обеспеченность");

  const safeName =
    fileName ||
    `Обеспеченность_${formatReportDateRu(reportDate).replace(/\./g, "-")}.xlsx`;
  XLSX.writeFile(wb, safeName);
}
